/**
 * Veyronix CRM
 * ============
 * 
 * Full CRM with Contacts, Accounts, Deals (Pipeline), Activities, Dashboard, and Automations.
 * Features drag-and-drop Kanban, inline editing, AI summaries, and POS integration.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft,
  Users,
  Building2,
  TrendingUp,
  ListTodo,
  LayoutDashboard,
  Zap,
  Search,
  Filter,
  Plus,
  MoreHorizontal,
  Phone,
  Mail,
  Calendar,
  Clock,
  CheckCircle2,
  Circle,
  ChevronRight,
  X,
  Edit3,
  Trash2,
  Sparkles,
  GripVertical,
  DollarSign,
  Target,
  Activity,
  AlertCircle,
  Loader2,
  RefreshCw,
  Download,
  Send,
  MessageSquare,
  FileText,
  Video,
  ExternalLink,
  Star,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Settings,
  Upload,
  FileSpreadsheet,
  Check,
  Link2,
} from "lucide-react";

const CRMPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  // Tab state
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Data states
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [pipelineData, setPipelineData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [reportsData, setReportsData] = useState(null);
  const [reportsPeriod, setReportsPeriod] = useState("30d");
  
  // UI states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(null); // 'contact', 'account', 'deal', 'activity'
  const [pipelineView, setPipelineView] = useState("kanban"); // 'kanban' or 'table'
  const [filterStage, setFilterStage] = useState(null);
  const [filterType, setFilterType] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Form states
  const [formData, setFormData] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  
  // Drag state for Kanban
  const [draggedDeal, setDraggedDeal] = useState(null);
  
  // CSV Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [autoMatchPOS, setAutoMatchPOS] = useState(true);
  const fileInputRef = useRef(null);
  
  // Birthday Loyalty state
  const [birthdayLoading, setBirthdayLoading] = useState(false);
  const [birthdayResult, setBirthdayResult] = useState(null);
  
  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "contacts", label: "Contacts", icon: Users },
    { id: "accounts", label: "Accounts", icon: Building2 },
    { id: "deals", label: "Deals", icon: Target },
    { id: "activities", label: "Activities", icon: ListTodo },
    { id: "automations", label: "Automations", icon: Zap },
    { id: "reports", label: "Reports", icon: BarChart3 },
  ];
  
  const stages = [
    { id: "lead", name: "Lead", color: "#94a3b8" },
    { id: "qualified", name: "Qualified", color: "#3b82f6" },
    { id: "proposal", name: "Proposal", color: "#8b5cf6" },
    { id: "negotiation", name: "Negotiation", color: "#f59e0b" },
    { id: "won", name: "Won", color: "#22c55e" },
    { id: "lost", name: "Lost", color: "#ef4444" },
  ];
  
  const lifecycleStages = ["lead", "prospect", "customer", "churned"];
  const activityTypes = ["call", "email", "meeting", "task", "note"];
  
  // ==================== DATA FETCHING ====================
  
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/crm/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboardData(data);
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    }
  }, [token]);
  
  const fetchContacts = useCallback(async (search = "", pageNum = 1) => {
    try {
      const params = new URLSearchParams({ page: pageNum, limit: 25 });
      if (search) params.set("search", search);
      
      const res = await fetch(`${API}/crm/contacts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContacts(data.contacts || []);
        setTotalPages(data.total_pages || 1);
      }
    } catch (err) {
      console.error("Contacts fetch error:", err);
    }
  }, [token]);
  
  const fetchAccounts = useCallback(async (search = "", pageNum = 1) => {
    try {
      const params = new URLSearchParams({ page: pageNum, limit: 25 });
      if (search) params.set("search", search);
      
      const res = await fetch(`${API}/crm/accounts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAccounts(data.accounts || []);
        setTotalPages(data.total_pages || 1);
      }
    } catch (err) {
      console.error("Accounts fetch error:", err);
    }
  }, [token]);
  
  const fetchDeals = useCallback(async (search = "", pageNum = 1, stage = null) => {
    try {
      const params = new URLSearchParams({ page: pageNum, limit: 50 });
      if (search) params.set("search", search);
      if (stage) params.set("stage", stage);
      
      const res = await fetch(`${API}/crm/deals?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDeals(data.deals || []);
        setTotalPages(data.total_pages || 1);
      }
    } catch (err) {
      console.error("Deals fetch error:", err);
    }
  }, [token]);
  
  const fetchPipeline = useCallback(async () => {
    try {
      const res = await fetch(`${API}/crm/deals/pipeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPipelineData(data);
      }
    } catch (err) {
      console.error("Pipeline fetch error:", err);
    }
  }, [token]);
  
  const fetchActivities = useCallback(async (type = null, pageNum = 1) => {
    try {
      const params = new URLSearchParams({ page: pageNum, limit: 25 });
      if (type) params.set("type_filter", type);
      
      const res = await fetch(`${API}/crm/activities?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActivities(data.activities || []);
        setTotalPages(data.total_pages || 1);
      }
    } catch (err) {
      console.error("Activities fetch error:", err);
    }
  }, [token]);
  
  const fetchAutomations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/crm/automations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAutomations(data.automations || []);
      }
    } catch (err) {
      console.error("Automations fetch error:", err);
    }
  }, [token]);
  
  const fetchReports = useCallback(async (period = "30d") => {
    try {
      const res = await fetch(`${API}/crm/reports?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReportsData(data);
      }
    } catch (err) {
      console.error("Reports fetch error:", err);
    }
  }, [token]);
  
  const seedDemoData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/crm/seed-demo-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (!data.skipped) {
          toast.success("Demo data seeded successfully!");
        }
        loadTabData();
      }
    } catch (err) {
      console.error("Seed error:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const loadTabData = useCallback(async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case "dashboard":
          await fetchDashboard();
          break;
        case "contacts":
          await fetchContacts(searchQuery, page);
          break;
        case "accounts":
          await fetchAccounts(searchQuery, page);
          break;
        case "deals":
          await Promise.all([fetchDeals(searchQuery, page, filterStage), fetchPipeline()]);
          break;
        case "activities":
          await fetchActivities(filterType, page);
          break;
        case "automations":
          await fetchAutomations();
          break;
        case "reports":
          await fetchReports(reportsPeriod);
          break;
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchQuery, page, filterStage, filterType, reportsPeriod, fetchDashboard, fetchContacts, fetchAccounts, fetchDeals, fetchPipeline, fetchActivities, fetchAutomations, fetchReports]);
  
  useEffect(() => {
    loadTabData();
  }, [loadTabData]);
  
  useEffect(() => {
    // Auto-seed demo data on first load
    seedDemoData();
  }, []);
  
  // ==================== CRUD OPERATIONS ====================
  
  const createEntity = async (type, data) => {
    try {
      const res = await fetch(`${API}/crm/${type}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        toast.success(`${type.slice(0, -1).charAt(0).toUpperCase() + type.slice(1, -1)} created!`);
        setShowCreateModal(null);
        setFormData({});
        loadTabData();
        return true;
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create");
        return false;
      }
    } catch (err) {
      toast.error("Error creating record");
      return false;
    }
  };
  
  const updateEntity = async (type, id, data) => {
    try {
      const idField = type === "contacts" ? "contact_id" : type === "accounts" ? "account_id" : type === "deals" ? "deal_id" : "activity_id";
      const res = await fetch(`${API}/crm/${type}/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        toast.success("Updated successfully!");
        loadTabData();
        return true;
      }
      return false;
    } catch (err) {
      toast.error("Error updating");
      return false;
    }
  };
  
  const deleteEntity = async (type, id) => {
    if (!confirm("Are you sure you want to delete this?")) return;
    
    try {
      const res = await fetch(`${API}/crm/${type}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Deleted successfully!");
        setShowSidePanel(false);
        setSelectedItem(null);
        loadTabData();
      }
    } catch (err) {
      toast.error("Error deleting");
    }
  };
  
  const updateDealStage = async (dealId, newStage) => {
    try {
      const res = await fetch(`${API}/crm/deals/${dealId}/stage?stage=${newStage}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`Deal moved to ${stages.find(s => s.id === newStage)?.name}`);
        fetchPipeline();
        fetchDeals();
      }
    } catch (err) {
      toast.error("Failed to update deal stage");
    }
  };
  
  const completeActivity = async (activityId) => {
    try {
      const res = await fetch(`${API}/crm/activities/${activityId}/complete`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Activity completed!");
        loadTabData();
      }
    } catch (err) {
      toast.error("Error completing activity");
    }
  };
  
  const toggleAutomation = async (automationId) => {
    try {
      const res = await fetch(`${API}/crm/automations/${automationId}/toggle`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchAutomations();
      }
    } catch (err) {
      toast.error("Error toggling automation");
    }
  };
  
  // ==================== BIRTHDAY LOYALTY ====================
  
  const triggerBirthdayEmails = async () => {
    setBirthdayLoading(true);
    setBirthdayResult(null);
    try {
      const res = await fetch(`${API}/loyalty/birthday/scan`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setBirthdayResult(data);
        if (data.emails_sent > 0) {
          toast.success(`Sent ${data.emails_sent} birthday emails with ${data.discount_code} discount!`);
        } else if (data.birthdays_found === 0) {
          toast.info("No customers with birthdays today");
        } else {
          toast.info("Birthday emails already sent for today's birthdays");
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to send birthday emails");
      }
    } catch (err) {
      toast.error("Error triggering birthday emails");
    } finally {
      setBirthdayLoading(false);
    }
  };
  
  // ==================== AI FEATURES ====================
  
  const generateAISummary = async (entityType, entityId) => {
    setAiLoading(true);
    setAiResult(null);
    try {
      const res = await fetch(`${API}/crm/ai/summary`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ entity_type: entityType, entity_id: entityId })
      });
      if (res.ok) {
        const data = await res.json();
        setAiResult({ type: "summary", content: data.summary });
        toast.success(`AI Summary generated (${data.credits_used} credits)`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "AI generation failed");
      }
    } catch (err) {
      toast.error("AI generation error");
    } finally {
      setAiLoading(false);
    }
  };
  
  const generateAIEmail = async (contactId, context = "") => {
    setAiLoading(true);
    setAiResult(null);
    try {
      const res = await fetch(`${API}/crm/ai/email-draft`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ contact_id: contactId, context, tone: "professional" })
      });
      if (res.ok) {
        const data = await res.json();
        setAiResult({ type: "email", content: data.email });
        toast.success(`Email draft generated (${data.credits_used} credits)`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "AI generation failed");
      }
    } catch (err) {
      toast.error("AI generation error");
    } finally {
      setAiLoading(false);
    }
  };
  
  // ==================== DRAG AND DROP ====================
  
  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = "move";
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  
  const handleDrop = (e, newStage) => {
    e.preventDefault();
    if (draggedDeal && draggedDeal.stage !== newStage) {
      updateDealStage(draggedDeal.deal_id, newStage);
    }
    setDraggedDeal(null);
  };
  
  // ==================== RENDER HELPERS ====================
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(amount || 0);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };
  
  const getStageColor = (stageId) => {
    return stages.find(s => s.id === stageId)?.color || "#94a3b8";
  };
  
  const getActivityIcon = (type) => {
    switch (type) {
      case "call": return Phone;
      case "email": return Mail;
      case "meeting": return Video;
      case "task": return ListTodo;
      case "note": return FileText;
      default: return Activity;
    }
  };
  
  // ==================== SIDE PANEL ====================
  
  const openSidePanel = async (type, id) => {
    try {
      const res = await fetch(`${API}/crm/${type}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedItem({ type, data });
        setShowSidePanel(true);
        setAiResult(null);
      }
    } catch (err) {
      toast.error("Error loading details");
    }
  };
  
  const renderSidePanel = () => {
    if (!selectedItem) return null;
    
    const { type, data } = selectedItem;
    const mainEntity = data[type.slice(0, -1)] || data;
    
    return (
      <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <h2 className="font-semibold text-lg text-gray-800">
            {type === "contacts" ? mainEntity.name : type === "accounts" ? mainEntity.company_name : type === "deals" ? mainEntity.title : mainEntity.title}
          </h2>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => deleteEntity(type, mainEntity[`${type.slice(0, -1)}_id`])}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowSidePanel(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Main Info */}
          <div className="space-y-3">
            {type === "contacts" && (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-bold text-xl">
                    {mainEntity.name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{mainEntity.name}</p>
                    <p className="text-sm text-gray-500">{mainEntity.job_title || "No title"} at {mainEntity.company || "No company"}</p>
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-1"
                      style={{ backgroundColor: `${getStageColor(mainEntity.lifecycle_stage)}20`, color: getStageColor(mainEntity.lifecycle_stage) }}>
                      {mainEntity.lifecycle_stage}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {mainEntity.email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="h-4 w-4 text-gray-400" />
                      {mainEntity.email}
                    </div>
                  )}
                  {mainEntity.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="h-4 w-4 text-gray-400" />
                      {mainEntity.phone}
                    </div>
                  )}
                </div>
                
                {/* POS Data Section */}
                {(mainEntity.total_spent > 0 || mainEntity.visit_count > 0 || mainEntity.pos_customer_id) && (
                  <div className="mt-4 p-3 bg-purple-50 rounded-xl border border-purple-100">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">POS Customer Data</span>
                      {mainEntity.pos_customer_id && (
                        <span className="ml-auto px-2 py-0.5 bg-purple-200 text-purple-700 text-xs rounded-full">Linked</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-lg font-bold text-purple-700">
                          £{((mainEntity.total_spent || 0) / 100).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">Total Spend</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-lg font-bold text-purple-700">{mainEntity.visit_count || 0}</p>
                        <p className="text-xs text-gray-500">Store Visits</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {type === "deals" && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-xl text-gray-800">{formatCurrency(mainEntity.amount)}</p>
                    <p className="text-sm text-gray-500">{mainEntity.contact_name || "No contact"}</p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{ backgroundColor: `${getStageColor(mainEntity.stage)}20`, color: getStageColor(mainEntity.stage) }}>
                    {stages.find(s => s.id === mainEntity.stage)?.name || mainEntity.stage}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Probability</p>
                    <p className="font-medium">{mainEntity.probability}%</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Close Date</p>
                    <p className="font-medium">{formatDate(mainEntity.close_date)}</p>
                  </div>
                </div>
              </>
            )}
            
            {type === "accounts" && (
              <>
                <div>
                  <p className="font-semibold text-xl text-gray-800">{mainEntity.company_name}</p>
                  <p className="text-sm text-gray-500">{mainEntity.sector || "No sector"} • {mainEntity.size || "Unknown"} size</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Total Deal Value</p>
                    <p className="font-medium">{formatCurrency(mainEntity.total_deal_value)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Contacts</p>
                    <p className="font-medium">{mainEntity.contact_count || 0}</p>
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* AI Actions */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4">
            <h3 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-indigo-500" />
              AI Actions (5 credits each)
            </h3>
            <div className="flex flex-wrap gap-2">
              <Button 
                size="sm" 
                variant="outline" 
                className="bg-white"
                onClick={() => generateAISummary(type.slice(0, -1), mainEntity[`${type.slice(0, -1)}_id`])}
                disabled={aiLoading}
              >
                {aiLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                AI Summary
              </Button>
              {type === "contacts" && (
                <Button 
                  size="sm" 
                  variant="outline"
                  className="bg-white"
                  onClick={() => generateAIEmail(mainEntity.contact_id)}
                  disabled={aiLoading}
                >
                  <Mail className="h-3 w-3 mr-1" />
                  Draft Email
                </Button>
              )}
            </div>
            {aiResult && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200 text-sm whitespace-pre-wrap">
                {aiResult.content}
              </div>
            )}
          </div>
          
          {/* Related Deals */}
          {data.deals && data.deals.length > 0 && (
            <div>
              <h3 className="font-medium text-sm text-gray-700 mb-2">Related Deals ({data.deals.length})</h3>
              <div className="space-y-2">
                {data.deals.slice(0, 5).map(deal => (
                  <div key={deal.deal_id} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-gray-800">{deal.title}</p>
                      <p className="text-xs text-gray-500">{formatCurrency(deal.amount)}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: `${getStageColor(deal.stage)}20`, color: getStageColor(deal.stage) }}>
                      {deal.stage}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Activity Timeline */}
          {data.activities && data.activities.length > 0 && (
            <div>
              <h3 className="font-medium text-sm text-gray-700 mb-2">Activity Timeline ({data.activities.length})</h3>
              <div className="space-y-3">
                {data.activities.slice(0, 10).map(activity => {
                  const Icon = getActivityIcon(activity.type);
                  return (
                    <div key={activity.activity_id} className="flex gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${activity.completed ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <Icon className={`h-4 w-4 ${activity.completed ? 'text-green-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-800">{activity.title}</p>
                        <p className="text-xs text-gray-500">{formatDate(activity.created_at)} • {activity.type}</p>
                        {activity.description && (
                          <p className="text-xs text-gray-600 mt-1">{activity.description}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-2">
          <Button 
            size="sm" 
            className="flex-1"
            onClick={() => {
              setShowCreateModal("activities");
              setFormData({ 
                related_contact_id: type === "contacts" ? mainEntity.contact_id : null,
                related_deal_id: type === "deals" ? mainEntity.deal_id : null,
                related_account_id: type === "accounts" ? mainEntity.account_id : null,
              });
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Activity
          </Button>
          {type === "contacts" && (
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => {
                setShowCreateModal("deals");
                setFormData({ primary_contact_id: mainEntity.contact_id });
              }}
            >
              <Target className="h-4 w-4 mr-1" />
              New Deal
            </Button>
          )}
        </div>
      </div>
    );
  };
  
  // ==================== CREATE MODAL ====================
  
  const renderCreateModal = () => {
    if (!showCreateModal) return null;
    
    const handleSubmit = (e) => {
      e.preventDefault();
      createEntity(showCreateModal, formData);
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-6 w-[500px] max-h-[80vh] overflow-y-auto shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-800">
              New {showCreateModal.slice(0, -1).charAt(0).toUpperCase() + showCreateModal.slice(1, -1)}
            </h2>
            <Button size="sm" variant="ghost" onClick={() => { setShowCreateModal(null); setFormData({}); }}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {showCreateModal === "contacts" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <Input 
                    value={formData.name || ""} 
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="John Smith"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <Input 
                      type="email"
                      value={formData.email || ""} 
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <Input 
                      value={formData.phone || ""} 
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+44 7700 900000"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                    <Input 
                      value={formData.company || ""} 
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      placeholder="Company Ltd"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                    <Input 
                      value={formData.job_title || ""} 
                      onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                      placeholder="CEO"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lifecycle Stage</label>
                  <select 
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={formData.lifecycle_stage || "lead"}
                    onChange={(e) => setFormData({...formData, lifecycle_stage: e.target.value})}
                  >
                    {lifecycleStages.map(s => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            
            {showCreateModal === "accounts" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <Input 
                    value={formData.company_name || ""} 
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    required
                    placeholder="Acme Corporation"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sector</label>
                    <Input 
                      value={formData.sector || ""} 
                      onChange={(e) => setFormData({...formData, sector: e.target.value})}
                      placeholder="Technology"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                    <select 
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={formData.size || ""}
                      onChange={(e) => setFormData({...formData, size: e.target.value})}
                    >
                      <option value="">Select size</option>
                      <option value="small">Small (1-50)</option>
                      <option value="medium">Medium (51-200)</option>
                      <option value="large">Large (201-1000)</option>
                      <option value="enterprise">Enterprise (1000+)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <Input 
                    value={formData.website || ""} 
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    placeholder="https://company.com"
                  />
                </div>
              </>
            )}
            
            {showCreateModal === "deals" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deal Title *</label>
                  <Input 
                    value={formData.title || ""} 
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    placeholder="New Enterprise Deal"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (GBP)</label>
                    <Input 
                      type="number"
                      value={formData.amount || ""} 
                      onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})}
                      placeholder="10000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                    <select 
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={formData.stage || "lead"}
                      onChange={(e) => setFormData({...formData, stage: e.target.value})}
                    >
                      {stages.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Probability (%)</label>
                    <Input 
                      type="number"
                      min="0"
                      max="100"
                      value={formData.probability || 10} 
                      onChange={(e) => setFormData({...formData, probability: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Close Date</label>
                    <Input 
                      type="date"
                      value={formData.close_date || ""} 
                      onChange={(e) => setFormData({...formData, close_date: e.target.value})}
                    />
                  </div>
                </div>
              </>
            )}
            
            {showCreateModal === "activities" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <Input 
                    value={formData.title || ""} 
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    placeholder="Follow-up call"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select 
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={formData.type || "task"}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                    >
                      {activityTypes.map(t => (
                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                    <Input 
                      type="datetime-local"
                      value={formData.due_date || ""} 
                      onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea 
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                    value={formData.description || ""} 
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Add notes..."
                  />
                </div>
              </>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setShowCreateModal(null); setFormData({}); }}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                Create
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  // ==================== CSV IMPORT ====================
  
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }
    
    setCsvFile(file);
    setImportLoading(true);
    
    try {
      const text = await file.text();
      
      const res = await fetch(`${API}/crm/contacts/csv/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ csv_data: text })
      });
      
      if (res.ok) {
        const data = await res.json();
        setCsvPreview(data);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to parse CSV");
        setCsvFile(null);
      }
    } catch (err) {
      toast.error("Error reading CSV file");
      setCsvFile(null);
    } finally {
      setImportLoading(false);
    }
  };
  
  const handleImportConfirm = async () => {
    if (!csvPreview?.preview) return;
    
    setImportLoading(true);
    try {
      const res = await fetch(`${API}/crm/contacts/csv/import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          contacts: csvPreview.preview,
          auto_match_pos: autoMatchPOS
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Imported ${data.imported} contacts! ${data.pos_linked > 0 ? `(${data.pos_linked} linked to POS)` : ''}`);
        setShowImportModal(false);
        setCsvFile(null);
        setCsvPreview(null);
        loadTabData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Import failed");
      }
    } catch (err) {
      toast.error("Error importing contacts");
    } finally {
      setImportLoading(false);
    }
  };
  
  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API}/crm/contacts/csv/template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([data.template], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'contacts_template.csv';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error("Error downloading template");
    }
  };
  
  const exportToCSV = async (entityType) => {
    try {
      const endpoint = entityType === "contacts" ? "/crm/export/contacts" : "/crm/export/deals";
      const res = await fetch(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${data.count} ${entityType}`);
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      toast.error("Error exporting data");
    }
  };
  
  const renderImportModal = () => {
    if (!showImportModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-6 w-[700px] max-h-[85vh] overflow-hidden flex flex-col shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Import Contacts from CSV</h2>
              <p className="text-sm text-gray-500 mt-1">Upload a CSV file to bulk import contacts</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => { setShowImportModal(false); setCsvFile(null); setCsvPreview(null); }}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {!csvPreview ? (
            <div className="flex-1">
              {/* Upload Area */}
              <div 
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-50'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-50'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-50');
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    const event = { target: { files: [file] } };
                    handleFileSelect(event);
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {importLoading ? (
                  <Loader2 className="h-12 w-12 text-indigo-500 mx-auto animate-spin" />
                ) : (
                  <>
                    <FileSpreadsheet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="font-medium text-gray-700">Drop your CSV file here or click to browse</p>
                    <p className="text-sm text-gray-500 mt-2">Supports .csv files with headers</p>
                  </>
                )}
              </div>
              
              {/* Template Download */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-700">Need a template?</p>
                    <p className="text-sm text-gray-500">Download our CSV template with all supported columns</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={downloadTemplate}>
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </Button>
                </div>
              </div>
              
              {/* Column Info */}
              <div className="mt-4 text-sm text-gray-600">
                <p className="font-medium mb-2">Supported columns:</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">name*</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">email</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">phone</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">company</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">job_title</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">lifecycle_stage</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">notes</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">source</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-indigo-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-indigo-600">{csvPreview.summary.total_rows}</p>
                  <p className="text-xs text-gray-600">Total Rows</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{csvPreview.summary.new_contacts}</p>
                  <p className="text-xs text-gray-600">New Contacts</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-600">{csvPreview.summary.duplicates}</p>
                  <p className="text-xs text-gray-600">Duplicates</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-purple-600">{csvPreview.summary.pos_matches}</p>
                  <p className="text-xs text-gray-600">POS Matches</p>
                </div>
              </div>
              
              {/* Auto-match POS toggle */}
              {csvPreview.summary.pos_matches > 0 && (
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg mb-4">
                  <input
                    type="checkbox"
                    id="autoMatchPOS"
                    checked={autoMatchPOS}
                    onChange={(e) => setAutoMatchPOS(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <label htmlFor="autoMatchPOS" className="flex-1">
                    <p className="font-medium text-gray-700 text-sm">Auto-link POS customer data</p>
                    <p className="text-xs text-gray-500">Link {csvPreview.summary.pos_matches} contacts to existing POS customers (spend history, visits)</p>
                  </label>
                  <Link2 className="h-5 w-5 text-purple-500" />
                </div>
              )}
              
              {/* Preview Table */}
              <div className="flex-1 overflow-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Name</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Email</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Company</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Stage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {csvPreview.preview.slice(0, 50).map((contact, idx) => (
                      <tr key={idx} className={contact.is_duplicate ? 'bg-amber-50/50' : ''}>
                        <td className="py-2 px-3">
                          {contact.is_duplicate ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                              <AlertCircle className="h-3 w-3" />
                              Duplicate
                            </span>
                          ) : contact.pos_match ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">
                              <Link2 className="h-3 w-3" />
                              POS Match
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                              <Check className="h-3 w-3" />
                              New
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 font-medium text-gray-800">{contact.name}</td>
                        <td className="py-2 px-3 text-gray-600">{contact.email || '-'}</td>
                        <td className="py-2 px-3 text-gray-600">{contact.company || '-'}</td>
                        <td className="py-2 px-3">
                          <span className="px-2 py-0.5 rounded-full text-xs capitalize bg-gray-100 text-gray-600">
                            {contact.lifecycle_stage}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {csvPreview.preview.length > 50 && (
                  <div className="p-3 text-center text-sm text-gray-500 bg-gray-50">
                    Showing 50 of {csvPreview.preview.length} contacts
                  </div>
                )}
              </div>
              
              {/* Errors */}
              {csvPreview.errors.length > 0 && (
                <div className="mt-3 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-700">{csvPreview.errors.length} rows with errors (skipped):</p>
                  <ul className="text-xs text-red-600 mt-1">
                    {csvPreview.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>Row {err.row}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {/* Footer Actions */}
          <div className="flex gap-3 pt-4 mt-4 border-t border-gray-200">
            {csvPreview ? (
              <>
                <Button variant="outline" className="flex-1" onClick={() => { setCsvFile(null); setCsvPreview(null); }}>
                  Choose Different File
                </Button>
                <Button 
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700" 
                  onClick={handleImportConfirm}
                  disabled={importLoading || csvPreview.summary.new_contacts === 0}
                >
                  {importLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Import {csvPreview.summary.new_contacts} Contacts
                </Button>
              </>
            ) : (
              <Button variant="outline" className="flex-1" onClick={() => setShowImportModal(false)}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // ==================== MAIN RENDER ====================
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950 text-gray-900 dark:text-white flex" data-testid="crm-page">
      {/* Sidebar */}
      <div className="w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col shadow-sm">
        {/* Brand Header */}
        <div className="p-4 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 dark:from-indigo-500 dark:via-violet-500 dark:to-fuchsia-400">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate("/merchant")}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-white" />
            </button>
            <div>
              <h1 className="font-bold text-lg text-white">Veyronix CRM</h1>
              <p className="text-xs text-indigo-100">Customer Management</p>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setPage(1); setSearchQuery(""); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isActive 
                    ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium" 
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className={`h-5 w-5 ${isActive ? "text-indigo-600 dark:text-indigo-400" : "text-gray-400"}`} />
                {tab.label}
              </button>
            );
          })}
        </nav>
        
        {/* Quick Add */}
        <div className="p-3 border-t border-gray-200">
          <Button 
            className="w-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 hover:opacity-90 text-white"
            onClick={() => setShowCreateModal(activeTab === "dashboard" ? "contacts" : activeTab)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Quick Add
          </Button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <div className="h-16 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800 px-6 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
              {tabs.find(t => t.id === activeTab)?.label}
            </h2>
            {(activeTab === "contacts" || activeTab === "accounts" || activeTab === "deals" || activeTab === "activities") && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  placeholder={`Search ${activeTab}...`}
                  className="pl-9 w-64 dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {activeTab === "contacts" && (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowImportModal(true)} className="dark:border-gray-700 dark:text-gray-300">
                  <Upload className="h-4 w-4 mr-2" />
                  Import CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportToCSV("contacts")} className="dark:border-gray-700 dark:text-gray-300">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </>
            )}
            {activeTab === "deals" && (
              <>
                <Button variant="outline" size="sm" onClick={() => exportToCSV("deals")} className="dark:border-gray-700 dark:text-gray-300">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button 
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${pipelineView === "kanban" ? "bg-white shadow text-gray-800" : "text-gray-600"}`}
                    onClick={() => setPipelineView("kanban")}
                  >
                    Kanban
                  </button>
                  <button 
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${pipelineView === "table" ? "bg-white shadow text-gray-800" : "text-gray-600"}`}
                    onClick={() => setPipelineView("table")}
                  >
                    Table
                  </button>
                </div>
              </>
            )}
            <Button variant="outline" size="sm" onClick={loadTabData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Content Area */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <>
              {/* Dashboard */}
              {activeTab === "dashboard" && dashboardData && (
                <div className="space-y-6">
                  {/* CRM Demo Video + Stats Grid */}
                  <div className="grid grid-cols-5 gap-4">
                    {/* Video Card - Spans 1 column */}
                    <div className="col-span-1 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl overflow-hidden shadow-sm">
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Video className="h-4 w-4 text-white" />
                          <span className="text-white text-xs font-medium">CRM Demo</span>
                        </div>
                        <div className="aspect-video rounded-lg overflow-hidden bg-black/20">
                          <video
                            src={`${API.replace('/api', '')}/api/ai/library/video/gen_6bc7d08604f2`}
                            className="w-full h-full object-cover"
                            controls
                            muted
                            poster="https://placehold.co/400x225/4f46e5/ffffff?text=CRM+Demo"
                            data-testid="crm-demo-video-embed"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Stats Cards - Span 4 columns */}
                    <div className="col-span-4 grid grid-cols-4 gap-4">
                      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 rounded-lg bg-indigo-100">
                            <Users className="h-5 w-5 text-indigo-600" />
                          </div>
                          <span className="text-gray-500 text-sm">Total Contacts</span>
                        </div>
                        <p className="text-3xl font-bold text-gray-800">{dashboardData.stats.total_contacts}</p>
                      </div>
                      
                      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 rounded-lg bg-purple-100">
                            <Target className="h-5 w-5 text-purple-600" />
                          </div>
                          <span className="text-gray-500 text-sm">Active Deals</span>
                        </div>
                        <p className="text-3xl font-bold text-purple-600">{dashboardData.stats.active_deals}</p>
                      </div>
                      
                      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 rounded-lg bg-green-100">
                            <DollarSign className="h-5 w-5 text-green-600" />
                          </div>
                          <span className="text-gray-500 text-sm">Pipeline Value</span>
                        </div>
                        <p className="text-3xl font-bold text-green-600">{formatCurrency(dashboardData.stats.pipeline_value)}</p>
                      </div>
                      
                      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 rounded-lg bg-amber-100">
                            <TrendingUp className="h-5 w-5 text-amber-600" />
                          </div>
                          <span className="text-gray-500 text-sm">Won This Month</span>
                        </div>
                        <p className="text-3xl font-bold text-amber-600">{formatCurrency(dashboardData.stats.won_this_month)}</p>
                        <p className="text-xs text-gray-500 mt-1">{dashboardData.stats.won_count} deals</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Secondary Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                      <h3 className="font-medium text-gray-700 mb-4">Activities Today</h3>
                      <div className="flex items-center justify-between">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-indigo-600">{dashboardData.stats.activities_due_today}</p>
                          <p className="text-xs text-gray-500">Due Today</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-red-500">{dashboardData.stats.overdue_activities}</p>
                          <p className="text-xs text-gray-500">Overdue</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                      <h3 className="font-medium text-gray-700 mb-4">Pipeline by Stage</h3>
                      <div className="space-y-2">
                        {stages.slice(0, 4).map(stage => {
                          const stageData = dashboardData.pipeline_by_stage[stage.id] || { count: 0, value: 0 };
                          return (
                            <div key={stage.id} className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                                {stage.name}
                              </span>
                              <span className="font-medium">{stageData.count} ({formatCurrency(stageData.value)})</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                      <h3 className="font-medium text-gray-700 mb-4">Contacts by Stage</h3>
                      <div className="space-y-2">
                        {lifecycleStages.map(stage => {
                          const count = dashboardData.contacts_by_lifecycle[stage] || 0;
                          return (
                            <div key={stage} className="flex items-center justify-between text-sm">
                              <span className="capitalize">{stage}</span>
                              <span className="font-medium">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Recent Activities */}
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                    <h3 className="font-medium text-gray-700 mb-4">Recent Activities</h3>
                    <div className="space-y-3">
                      {dashboardData.recent_activities.map(activity => {
                        const Icon = getActivityIcon(activity.type);
                        return (
                          <div key={activity.activity_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${activity.completed ? 'bg-green-100' : 'bg-indigo-100'}`}>
                              <Icon className={`h-4 w-4 ${activity.completed ? 'text-green-600' : 'text-indigo-600'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-800 truncate">{activity.title}</p>
                              <p className="text-xs text-gray-500">{activity.type} • {formatDate(activity.created_at)}</p>
                            </div>
                            {!activity.completed && (
                              <Button size="sm" variant="ghost" onClick={() => completeActivity(activity.activity_id)}>
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Birthday Loyalty Quick Actions */}
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-gray-700">Loyalty Quick Actions</h3>
                      <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">P4 Feature</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-pink-50 rounded-lg border border-pink-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                            <span className="text-lg" role="img" aria-label="birthday">🎂</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">Birthday Emails</p>
                            <p className="text-xs text-gray-500">Send 20% off coupons to customers with birthdays today</p>
                          </div>
                        </div>
                        <Button 
                          onClick={triggerBirthdayEmails}
                          disabled={birthdayLoading}
                          className="bg-pink-500 hover:bg-pink-600 text-white"
                          data-testid="trigger-birthday-btn"
                        >
                          {birthdayLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Send Emails
                            </>
                          )}
                        </Button>
                      </div>
                      
                      {birthdayResult && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-100 text-sm">
                          <p className="text-green-700">
                            <CheckCircle2 className="h-4 w-4 inline mr-2" />
                            Found {birthdayResult.birthdays_found} birthdays, sent {birthdayResult.emails_sent} emails
                            {birthdayResult.discount_code && (
                              <span className="ml-2 font-mono text-xs bg-green-100 px-2 py-0.5 rounded">
                                {birthdayResult.discount_code}
                              </span>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Contacts Table */}
              {activeTab === "contacts" && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Company</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Stage</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Last Activity</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {contacts.map(contact => (
                        <tr 
                          key={contact.contact_id} 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => openSidePanel("contacts", contact.contact_id)}
                          data-testid={`contact-row-${contact.contact_id}`}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-medium text-sm">
                                {contact.name?.charAt(0)?.toUpperCase() || "?"}
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{contact.name}</p>
                                <p className="text-xs text-gray-500">{contact.job_title || "No title"}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{contact.company || "-"}</td>
                          <td className="py-3 px-4 text-sm text-gray-600">{contact.email || "-"}</td>
                          <td className="py-3 px-4">
                            <span className="inline-block px-2 py-1 rounded-full text-xs font-medium capitalize"
                              style={{ backgroundColor: `${getStageColor(contact.lifecycle_stage)}20`, color: getStageColor(contact.lifecycle_stage) }}>
                              {contact.lifecycle_stage}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">{formatDate(contact.last_activity_at)}</td>
                          <td className="py-3 px-4">
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {contacts.length === 0 && (
                    <div className="text-center py-12">
                      <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No contacts found</p>
                      <Button className="mt-4" onClick={() => setShowCreateModal("contacts")}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Contact
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Accounts Table */}
              {activeTab === "accounts" && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Company</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Sector</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Contacts</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Deal Value</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Deals</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {accounts.map(account => (
                        <tr 
                          key={account.account_id} 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => openSidePanel("accounts", account.account_id)}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                                <Building2 className="h-5 w-5 text-gray-500" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{account.company_name}</p>
                                <p className="text-xs text-gray-500 capitalize">{account.size || "Unknown"} size</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{account.sector || "-"}</td>
                          <td className="py-3 px-4 text-sm text-gray-600">{account.contact_count || 0}</td>
                          <td className="py-3 px-4 text-sm font-medium text-gray-800">{formatCurrency(account.total_deal_value)}</td>
                          <td className="py-3 px-4 text-sm text-gray-600">{account.deal_count || 0}</td>
                          <td className="py-3 px-4">
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {accounts.length === 0 && (
                    <div className="text-center py-12">
                      <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No accounts found</p>
                      <Button className="mt-4" onClick={() => setShowCreateModal("accounts")}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Account
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Deals - Kanban or Table */}
              {activeTab === "deals" && (
                <>
                  {pipelineView === "kanban" && pipelineData ? (
                    <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: "calc(100vh - 200px)" }}>
                      {stages.map(stage => {
                        const stageData = pipelineData.pipeline[stage.id] || { deals: [], total_value: 0, count: 0 };
                        return (
                          <div 
                            key={stage.id}
                            className="flex-shrink-0 w-72 bg-gray-100 rounded-xl p-3"
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, stage.id)}
                          >
                            {/* Stage Header */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                                <span className="font-medium text-gray-700">{stage.name}</span>
                                <span className="text-xs bg-white px-2 py-0.5 rounded-full text-gray-500">{stageData.count}</span>
                              </div>
                              <span className="text-xs font-medium text-gray-500">{formatCurrency(stageData.total_value)}</span>
                            </div>
                            
                            {/* Deal Cards */}
                            <div className="space-y-2">
                              {stageData.deals.map(deal => (
                                <div 
                                  key={deal.deal_id}
                                  className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 cursor-grab hover:shadow-md transition-shadow"
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, deal)}
                                  onClick={() => openSidePanel("deals", deal.deal_id)}
                                  data-testid={`deal-card-${deal.deal_id}`}
                                >
                                  <div className="flex items-start gap-2 mb-2">
                                    <GripVertical className="h-4 w-4 text-gray-300 mt-0.5" />
                                    <p className="font-medium text-sm text-gray-800 flex-1">{deal.title}</p>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-lg font-bold text-gray-700">{formatCurrency(deal.amount)}</span>
                                    {deal.contact_name && (
                                      <span className="text-xs text-gray-500 truncate max-w-[100px]">{deal.contact_name}</span>
                                    )}
                                  </div>
                                  {deal.close_date && (
                                    <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                                      <Calendar className="h-3 w-3" />
                                      {formatDate(deal.close_date)}
                                    </div>
                                  )}
                                </div>
                              ))}
                              
                              {/* Add Deal Button */}
                              <button 
                                className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors text-sm flex items-center justify-center gap-2"
                                onClick={() => { setShowCreateModal("deals"); setFormData({ stage: stage.id }); }}
                              >
                                <Plus className="h-4 w-4" />
                                Add Deal
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Deal</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Contact</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Amount</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Stage</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Close Date</th>
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {deals.map(deal => (
                            <tr 
                              key={deal.deal_id} 
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => openSidePanel("deals", deal.deal_id)}
                            >
                              <td className="py-3 px-4">
                                <p className="font-medium text-gray-800">{deal.title}</p>
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600">{deal.contact_name || "-"}</td>
                              <td className="py-3 px-4 text-sm font-medium text-gray-800">{formatCurrency(deal.amount)}</td>
                              <td className="py-3 px-4">
                                <span className="inline-block px-2 py-1 rounded-full text-xs font-medium"
                                  style={{ backgroundColor: `${getStageColor(deal.stage)}20`, color: getStageColor(deal.stage) }}>
                                  {stages.find(s => s.id === deal.stage)?.name || deal.stage}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-500">{formatDate(deal.close_date)}</td>
                              <td className="py-3 px-4">
                                <ChevronRight className="h-4 w-4 text-gray-400" />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
              
              {/* Activities Table */}
              {activeTab === "activities" && (
                <div className="space-y-4">
                  {/* Type Filter */}
                  <div className="flex gap-2">
                    <button 
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${!filterType ? "bg-indigo-100 text-indigo-700" : "bg-white text-gray-600 border border-gray-200"}`}
                      onClick={() => setFilterType(null)}
                    >
                      All
                    </button>
                    {activityTypes.map(type => (
                      <button 
                        key={type}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${filterType === type ? "bg-indigo-100 text-indigo-700" : "bg-white text-gray-600 border border-gray-200"}`}
                        onClick={() => setFilterType(type)}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="w-10"></th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Activity</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Type</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Related To</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Due Date</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {activities.map(activity => {
                          const Icon = getActivityIcon(activity.type);
                          const isOverdue = !activity.completed && activity.due_date && new Date(activity.due_date) < new Date();
                          return (
                            <tr key={activity.activity_id} className="hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <button 
                                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                    activity.completed 
                                      ? "bg-green-500 border-green-500" 
                                      : "border-gray-300 hover:border-green-500"
                                  }`}
                                  onClick={() => !activity.completed && completeActivity(activity.activity_id)}
                                >
                                  {activity.completed && <CheckCircle2 className="h-3 w-3 text-white" />}
                                </button>
                              </td>
                              <td className="py-3 px-4">
                                <p className={`font-medium text-sm ${activity.completed ? "text-gray-400 line-through" : "text-gray-800"}`}>
                                  {activity.title}
                                </p>
                                {activity.description && (
                                  <p className="text-xs text-gray-500 truncate max-w-xs">{activity.description}</p>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2 text-sm text-gray-600 capitalize">
                                  <Icon className="h-4 w-4 text-gray-400" />
                                  {activity.type}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600">
                                {activity.contact_name || activity.deal_title || "-"}
                              </td>
                              <td className={`py-3 px-4 text-sm ${isOverdue ? "text-red-500 font-medium" : "text-gray-500"}`}>
                                {formatDate(activity.due_date)}
                                {isOverdue && <AlertCircle className="h-3 w-3 inline ml-1" />}
                              </td>
                              <td className="py-3 px-4">
                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                  activity.completed 
                                    ? "bg-green-100 text-green-700" 
                                    : isOverdue 
                                      ? "bg-red-100 text-red-700"
                                      : "bg-indigo-100 text-indigo-700"
                                }`}>
                                  {activity.completed ? "Completed" : isOverdue ? "Overdue" : "Pending"}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <Button size="sm" variant="ghost" onClick={() => deleteEntity("activities", activity.activity_id)}>
                                  <Trash2 className="h-4 w-4 text-gray-400" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    
                    {activities.length === 0 && (
                      <div className="text-center py-12">
                        <ListTodo className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No activities found</p>
                        <Button className="mt-4" onClick={() => setShowCreateModal("activities")}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Activity
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Automations */}
              {activeTab === "automations" && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                    <div className="flex items-center gap-3 mb-2">
                      <Zap className="h-6 w-6 text-purple-600" />
                      <h3 className="font-semibold text-lg text-gray-800">Workflow Automations</h3>
                    </div>
                    <p className="text-gray-600 text-sm">Automate repetitive tasks and trigger actions based on CRM events.</p>
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
                    {automations.map(automation => (
                      <div key={automation.automation_id} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${automation.enabled ? "bg-green-100" : "bg-gray-100"}`}>
                            <Zap className={`h-5 w-5 ${automation.enabled ? "text-green-600" : "text-gray-400"}`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{automation.name}</p>
                            <p className="text-sm text-gray-500">{automation.description || `Trigger: ${automation.trigger_type}`}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {automation.is_default && (
                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">Default</span>
                          )}
                          <button 
                            className={`relative w-12 h-6 rounded-full transition-colors ${automation.enabled ? "bg-green-500" : "bg-gray-300"}`}
                            onClick={() => toggleAutomation(automation.automation_id)}
                          >
                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${automation.enabled ? "left-7" : "left-1"}`} />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {automations.length === 0 && (
                      <div className="text-center py-12">
                        <Zap className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No automations configured</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Reports Tab */}
              {activeTab === "reports" && reportsData && (
                <div className="space-y-6">
                  {/* Period Selector */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-800">CRM Reports</h2>
                      <p className="text-sm text-gray-500">Performance analytics and insights</p>
                    </div>
                    <div className="flex gap-2">
                      {["7d", "30d", "90d", "365d"].map(period => (
                        <button
                          key={period}
                          onClick={() => { setReportsPeriod(period); fetchReports(period); }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            reportsPeriod === period 
                              ? "bg-indigo-600 text-white" 
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {period === "7d" ? "7 Days" : period === "30d" ? "30 Days" : period === "90d" ? "90 Days" : "1 Year"}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <TrendingUp className="h-5 w-5 text-green-600" />
                        </div>
                        <span className="text-sm text-gray-500">Total Revenue</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-800">{formatCurrency(reportsData.summary.total_revenue)}</p>
                      <p className="text-xs text-green-600 mt-1">{reportsData.deals.won.count} deals won</p>
                    </div>
                    
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <Target className="h-5 w-5 text-indigo-600" />
                        </div>
                        <span className="text-sm text-gray-500">Pipeline Value</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-800">{formatCurrency(reportsData.summary.total_pipeline_value)}</p>
                      <p className="text-xs text-indigo-600 mt-1">Active opportunities</p>
                    </div>
                    
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Users className="h-5 w-5 text-purple-600" />
                        </div>
                        <span className="text-sm text-gray-500">Win Rate</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-800">{reportsData.summary.win_rate}%</p>
                      <p className="text-xs text-purple-600 mt-1">Deal conversion</p>
                    </div>
                    
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <DollarSign className="h-5 w-5 text-amber-600" />
                        </div>
                        <span className="text-sm text-gray-500">Avg Deal Size</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-800">{formatCurrency(reportsData.summary.avg_deal_size)}</p>
                      <p className="text-xs text-amber-600 mt-1">Per won deal</p>
                    </div>
                  </div>
                  
                  {/* Charts Row */}
                  <div className="grid grid-cols-2 gap-6">
                    {/* Revenue Trend */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h3 className="font-semibold text-gray-800 mb-4">Revenue Trend</h3>
                      <div className="h-48 flex items-end justify-between gap-2">
                        {reportsData.charts.revenue_trend.map((month, idx) => {
                          const maxRevenue = Math.max(...reportsData.charts.revenue_trend.map(m => m.revenue), 1);
                          const height = (month.revenue / maxRevenue) * 100;
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                              <div 
                                className="w-full bg-gradient-to-t from-green-500 to-green-400 rounded-t transition-all hover:from-green-600 hover:to-green-500"
                                style={{ height: `${Math.max(height, 4)}%` }}
                                title={`£${month.revenue.toLocaleString()}`}
                              />
                              <span className="text-xs text-gray-500">{month.month}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Pipeline by Stage */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h3 className="font-semibold text-gray-800 mb-4">Pipeline by Stage</h3>
                      <div className="space-y-3">
                        {reportsData.charts.pipeline_by_stage.map((stage, idx) => {
                          const stageConfig = stages.find(s => s.id === stage.stage) || { color: "#94a3b8", name: stage.stage };
                          const maxValue = Math.max(...reportsData.charts.pipeline_by_stage.map(s => s.value), 1);
                          const width = (stage.value / maxValue) * 100;
                          return (
                            <div key={idx} className="flex items-center gap-3">
                              <span className="w-24 text-sm text-gray-600 capitalize">{stageConfig.name}</span>
                              <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full transition-all"
                                  style={{ width: `${Math.max(width, 2)}%`, backgroundColor: stageConfig.color }}
                                />
                              </div>
                              <span className="w-24 text-sm text-gray-800 text-right">£{stage.value.toLocaleString()}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Top Deals & Activity Stats */}
                  <div className="grid grid-cols-2 gap-6">
                    {/* Top Deals */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h3 className="font-semibold text-gray-800 mb-4">Top Deals</h3>
                      <div className="space-y-3">
                        {reportsData.top_deals.map((deal, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-800">{deal.title}</p>
                              <p className="text-sm text-gray-500">{deal.contact_name}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-green-600">£{deal.amount.toLocaleString()}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                                deal.stage === "won" ? "bg-green-100 text-green-700" :
                                deal.stage === "lost" ? "bg-red-100 text-red-700" :
                                "bg-indigo-100 text-indigo-700"
                              }`}>
                                {deal.stage}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Activity Breakdown */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h3 className="font-semibold text-gray-800 mb-4">Activity Breakdown</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-4 bg-indigo-50 rounded-xl">
                          <p className="text-3xl font-bold text-indigo-600">{reportsData.activities.total}</p>
                          <p className="text-sm text-gray-600">Total Activities</p>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-xl">
                          <p className="text-3xl font-bold text-green-600">{reportsData.activities.completion_rate}%</p>
                          <p className="text-sm text-gray-600">Completion Rate</p>
                        </div>
                      </div>
                      <div className="mt-4 space-y-2">
                        {reportsData.charts.activities_by_type.map((act, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <span className="capitalize text-gray-600">{act.type}</span>
                            <span className="font-medium text-gray-800">{act.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Contact Stats */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">Contact Analytics</h3>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-gray-50 rounded-xl">
                        <p className="text-2xl font-bold text-gray-800">{reportsData.contacts.total}</p>
                        <p className="text-sm text-gray-500">Total Contacts</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-xl">
                        <p className="text-2xl font-bold text-green-600">+{reportsData.contacts.new_this_period}</p>
                        <p className="text-sm text-gray-500">New This Period</p>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-xl">
                        <p className="text-2xl font-bold text-purple-600">{reportsData.contacts.by_stage?.customer || 0}</p>
                        <p className="text-sm text-gray-500">Customers</p>
                      </div>
                      <div className="text-center p-4 bg-amber-50 rounded-xl">
                        <p className="text-2xl font-bold text-amber-600">{formatCurrency(reportsData.contacts.total_customer_spend)}</p>
                        <p className="text-sm text-gray-500">Customer Spend</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (activeTab === "contacts" || activeTab === "accounts" || activeTab === "activities") && (
          <div className="h-14 border-t border-gray-200 bg-white px-6 flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* Side Panel */}
      {showSidePanel && renderSidePanel()}
      
      {/* Create Modal */}
      {renderCreateModal()}
      
      {/* Import Modal */}
      {renderImportModal()}
      
      {/* Overlay for Side Panel */}
      {showSidePanel && (
        <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setShowSidePanel(false)} />
      )}
    </div>
  );
};

export default CRMPage;
